import { createContext, useContext, useState, useEffect } from "react";
import { TravlDataViewModel } from "./ViewModels/TravlDataViewModel";
import Papa from "papaparse";
import { SurveyViewModel } from "./ViewModels/SurveyViewModel";

type TravlDataContextType = {
    siteData: TravlDataViewModel[];
    surveyData: SurveyViewModel[];
    setFilterSurveyCodes: (codes: string[]) => void;
    exportToCsv: <T extends Record<string, unknown>>(rows: T[], filename?: string) => void;
};

const TravlDataContext = createContext<TravlDataContextType | undefined>(undefined);

export const TravlDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [siteData, setSiteData] = useState<TravlDataViewModel[]>([]);
    const [surveyData, setSurveyData] = useState<SurveyViewModel[]>([]);
    const [filterSurveyCodes, setFilterSurveyCodes] = useState<string[]>([]);

    useEffect(() => {
        fetch("/TRAVLData/TRAVLSurveys.csv")
            .then(res => res.text())
            .then(csv => {
                Papa.parse<TravlDataViewModel>(csv, {
                    header: true,
                    skipEmptyLines: true,
                    complete: results => setSiteData(results.data),
                });
            });
    }, []);

    useEffect(() => {
        if (filterSurveyCodes.length === 0) {
            return;
        }
        fetch("/TRAVLData/TravlModeByTime.csv")
            .then(res => res.text())
            .then(csv => {
                const filteredRows: SurveyViewModel[] = [];

                Papa.parse<SurveyViewModel>(csv, {
                    header: true,
                    skipEmptyLines: true,
                    step: (row) => {
                        const record = row.data;
                        if (filterSurveyCodes.includes(record.SurveyCode)) {
                            filteredRows.push(record);
                        }
                    },
                    complete: () => setSurveyData(filteredRows)
                });
            });
    }, [filterSurveyCodes]);

    const exportToCsv = <T extends Record<string, unknown>>(rows: T[], filename?: string) => {
        if (!rows || rows.length === 0) return;

        const stamp = (() => {
            const d = new Date();
            const p = (n: number) => String(n).padStart(2, "0");
            return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
        })();

        const finalName = (() => {
            const base = (filename && filename.trim().length > 0) ? filename.trim() : `exports_${stamp}.csv`;
            return base.toLowerCase().endsWith(".csv") ? base : `${base}.csv`;
        })();

        const csv = Papa.unparse(rows);
        const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = finalName;
        a.rel = "noopener";
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <TravlDataContext.Provider value={{ siteData, surveyData, setFilterSurveyCodes, exportToCsv }}>
            {children}
        </TravlDataContext.Provider>
    );
};

export function useTravlData() {
    const ctx = useContext(TravlDataContext);
    if (!ctx) throw new Error("useTravlData must be used within a TravlDataProvider");
    return ctx;
}
